import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useHasAuthorityDocument } from "@/shared/hooks/useHasAuthorityDocument";
import { INTAKE_TASK_IDS } from "@/shared/constants/taskIds";

type StepStatus = "not_started" | "in_progress" | "completed" | "skipped" | undefined;

export interface IntakeStatus {
  hasCompletedIntro: boolean;
  hasCompletedLegalAuthority: boolean;
  hasAuthorityDocument: boolean;
  hasEstablishedAuthority: boolean;
  hasDismissedLegalAuthority: boolean;
  stepStatus: (taskId: string) => StepStatus;
  stepAnswers: (taskId: string) => Record<string, unknown> | null;
  resumeStep:
    | "welcome"
    | "death-certificate"
    | "scanning"
    | "confirm-details"
    | "authority"
    | "processing"
    | null;
  isFetched: boolean;
  isLoading: boolean;
  invalidate: () => void;
}

const STEP_ORDER: {
  taskId: string;
  currentStep: IntakeStatus["resumeStep"];
  nextStep: IntakeStatus["resumeStep"];
}[] = [
  {
    taskId: INTAKE_TASK_IDS.INTRO,
    currentStep: "death-certificate",
    nextStep: "death-certificate",
  },
  { taskId: INTAKE_TASK_IDS.DEATH_CERT, currentStep: "scanning", nextStep: "scanning" },
  {
    taskId: INTAKE_TASK_IDS.CONFIRM_DETAILS,
    currentStep: "confirm-details",
    nextStep: "authority",
  },
  { taskId: INTAKE_TASK_IDS.AUTHORITY, currentStep: "authority", nextStep: "processing" },
  { taskId: INTAKE_TASK_IDS.INVITES, currentStep: "processing", nextStep: "processing" },
];

export function useIntakeStatus(): IntakeStatus {
  const person = usePerson();
  const estateId = person?.estate_id;
  const queryClient = useQueryClient();
  const { hasAuthorityDocument, isFetched: isAuthorityDocFetched } =
    useHasAuthorityDocument(estateId);

  const { data, isPending, isFetched } = useQuery({
    queryKey: ["intakeStatus", estateId],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("estate_tasks")
        .select("task_id, status, answers")
        .eq("estate_id", estateId!)
        .in("task_id", Object.values(INTAKE_TASK_IDS));

      if (error) throw error;
      return rows ?? [];
    },
    enabled: !!estateId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const taskMap = new Map(data?.map((r) => [r.task_id, r]) ?? []);
  const introRow = taskMap.get(INTAKE_TASK_IDS.INTRO);
  const legalModalRow = taskMap.get(INTAKE_TASK_IDS.LEGAL_AUTHORITY);

  const stepStatus = (taskId: string): StepStatus =>
    (taskMap.get(taskId)?.status as StepStatus) ?? undefined;

  const stepAnswers = (taskId: string): Record<string, unknown> | null =>
    (taskMap.get(taskId)?.answers as Record<string, unknown> | null) ?? null;

  let resumeStep: IntakeStatus["resumeStep"] = null;
  if (data && data.length > 0) {
    resumeStep = "welcome";
    for (const { taskId, currentStep, nextStep } of STEP_ORDER) {
      const status = stepStatus(taskId);
      if (status === "completed") {
        resumeStep = nextStep;
      } else if (status === "in_progress") {
        resumeStep = currentStep;
        break;
      } else {
        break;
      }
    }
  }

  const hasCompletedLegalAuthority = legalModalRow?.status === "completed";

  return {
    hasCompletedIntro: introRow?.status === "completed",
    hasCompletedLegalAuthority,
    hasAuthorityDocument,
    hasEstablishedAuthority: hasCompletedLegalAuthority || hasAuthorityDocument,
    hasDismissedLegalAuthority:
      legalModalRow?.status === "skipped" ||
      !!(legalModalRow?.answers as Record<string, unknown> | null)?.dismissed,
    stepStatus,
    stepAnswers,
    resumeStep,
    isFetched: (!estateId || isFetched) && isAuthorityDocFetched,
    isLoading: !!estateId && isPending,
    invalidate: () => queryClient.invalidateQueries({ queryKey: ["intakeStatus", estateId] }),
  };
}
